import React from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowRight, PiggyBank, LineChart, Target, BarChart3 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { AppPage } from '../types/navigation';
import Roadmap from '../components/Roadmap';

interface MainPageProps {
  onNavigate: (page: AppPage) => void;
}

const MainPage: React.FC<MainPageProps> = ({ onNavigate }) => {
  return (
    <div className="flex min-h-svh flex-col items-center justify-center gap-6 bg-muted p-6 md:p-10">
      <div className="flex w-full max-w-4xl flex-col gap-8">
        <div className="text-center space-y-4">
          <h1 className="text-4xl font-bold tracking-tight sm:text-5xl">
            Personal Budget
          </h1>
          <p className="text-xl text-muted-foreground">
            Take control of your financial future with our powerful budgeting tools
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          <Card className="group hover:shadow-md transition-shadow">
            <CardHeader>
              <div className="flex items-center gap-2">
                <PiggyBank className="h-5 w-5 text-primary" />
                <CardTitle>Track Expenses</CardTitle>
              </div>
              <CardDescription>
                See Your Complete Financial Picture: Securely connect all your bank accounts and credit cards in one place using Plaid.
              </CardDescription>
            </CardHeader>
          </Card>

          <Card className="group hover:shadow-md transition-shadow">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Target className="h-5 w-5 text-primary" />
                <CardTitle>Set Budgets</CardTitle>
              </div>
              <CardDescription>
                Stop Juggling Logins: Access transactions from all your financial institutions within a single, easy-to-use app.
              </CardDescription>
            </CardHeader>
          </Card>

          <Card className="group hover:shadow-md transition-shadow">
            <CardHeader>
              <div className="flex items-center gap-2">
                <LineChart className="h-5 w-5 text-primary" />
                <CardTitle>Financial Goals</CardTitle>
              </div>
              <CardDescription>
                Your Entire Financial Life, Simplified: Effortlessly track balances and transactions across multiple accounts without switching apps.
              </CardDescription>
            </CardHeader>
          </Card>

          <Card className="group hover:shadow-md transition-shadow">
            <CardHeader>
              <div className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-primary" />
                <CardTitle>Reports & Insights</CardTitle>
              </div>
              <CardDescription>
                One App, All Your Finances: Bring clarity to your money by viewing everything together.
              </CardDescription>
            </CardHeader>
          </Card>
        </div>

        <Card className="mt-8">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center gap-4 text-center">
              <h2 className="text-2xl font-semibold tracking-tight">
                Ready to take control of your finances?
              </h2>
              <p className="text-muted-foreground max-w-md">
                Join thousands of users who are already managing their finances smarter with Personal Budget
              </p>
              <div className="flex flex-col sm:flex-row justify-center items-center gap-4 mt-4">
                <Button size="lg" onClick={() => onNavigate('login')}>
                  See Demo
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <Dialog>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="lg">View Our Roadmap</Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-[600px]">
                    <DialogHeader>
                      <DialogTitle>Development Roadmap</DialogTitle>
                    </DialogHeader>
                    <div className="mt-4 max-h-[70svh] overflow-y-auto pr-6">
                      <Roadmap />
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default MainPage; 